import type { HiredEquipmentGridRow } from '../types';
import { isHoursUnit } from './overtime';

export type RequiredGridField = 'date' | 'task' | 'vendor' | 'resource' | 'qty' | 'rate' | 'fuel' | 'description';

/** C3 – an hourly line cannot exceed 24 hours. Mirrors server HE_MAX_HOURS_PER_DAY. */
export const HE_MAX_HOURS_PER_DAY = 24;
/** M6 – warn when one resource passes 16 hours on one date. */
export const HE_SAME_DAY_WARN_HOURS = 16;
/** H8 – a fuel override, when used, is $0.01–$100.00 per hour. */
export const HE_FUEL_OVERRIDE_MIN = 0.01;
export const HE_FUEL_OVERRIDE_MAX = 100;
/** M23 – a line comment is at most 1,000 characters. */
export const HE_LINE_COMMENT_MAX = 1000;
/** M7 – a lump sum line's description is at most this long. */
export const HE_LUMP_SUM_DESCRIPTION_MAX = 200;

/** A line with no unit is stored as Hours, so it is checked as one. */
function effectiveUnit(unit: string | null | undefined): string {
  return unit?.trim() || 'Hours';
}

/**
 * C3 – what is wrong with this line's quantity, as a short inline message, or null. Same rules
 * the server enforces at submit (hired-equipment.pricing.ts quantityProblem).
 */
export function quantityProblem(row: Pick<HiredEquipmentGridRow, 'qty' | 'unit' | 'lumpSum'>): string | null {
  if (row.lumpSum) return null;
  if (row.qty == null || Number.isNaN(row.qty)) return null; // "missing" is the required-field check
  if (row.qty <= 0) return 'Must be greater than 0';
  if (isHoursUnit(effectiveUnit(row.unit)) && row.qty > HE_MAX_HOURS_PER_DAY) {
    return `Max ${HE_MAX_HOURS_PER_DAY} hours`;
  }
  if (isEachUnit(row.unit) && !Number.isInteger(row.qty)) return 'Whole numbers only';
  return null;
}

/** H8 – the fuel override message, or null. Only applies while the line is fuel-eligible. */
export function fuelOverrideProblem(
  row: Pick<HiredEquipmentGridRow, 'fuelSurchargeEligible' | 'fuelSurchargeOverridePerHour' | 'lumpSum'>,
): string | null {
  if (row.lumpSum || row.fuelSurchargeEligible !== true) return null;
  const value = row.fuelSurchargeOverridePerHour;
  if (value == null) return null;
  if (!Number.isFinite(value) || value < HE_FUEL_OVERRIDE_MIN || value > HE_FUEL_OVERRIDE_MAX) {
    return `$${HE_FUEL_OVERRIDE_MIN.toFixed(2)}–$${HE_FUEL_OVERRIDE_MAX.toFixed(2)} per hour`;
  }
  return null;
}

/** "ea" / "each" (any punctuation/case) — count units, whole numbers only. */
export function isEachUnit(unit: string | null | undefined): boolean {
  const normalized = (unit ?? '').trim().toLowerCase().replace(/[.\s]/g, '');
  return normalized === 'ea' || normalized === 'each';
}

export function qtyStepForUnit(unit: string | null | undefined): string {
  return isEachUnit(unit) ? '1' : '0.01';
}

function isNonIntegerQty(qty: number): boolean {
  return !Number.isInteger(qty);
}

export function isRequiredFieldMissing(row: HiredEquipmentGridRow, field: RequiredGridField): boolean {
  switch (field) {
    case 'date':
      return !row.date;
    case 'task':
      return row.taskId == null;
    case 'vendor':
      return row.vendorId == null;
    case 'resource':
      // A lump sum has no resource - "lump sum" IS the resource.
      if (row.lumpSum) return false;
      return row.resourceId == null && !row.resourceLabel;
    case 'qty':
      // A lump sum has no qty; it is normalized to 1 so the amount math works.
      if (row.lumpSum) return false;
      if (row.qty == null || Number.isNaN(row.qty)) return true;
      if (isEachUnit(row.unit) && isNonIntegerQty(row.qty)) return true;
      return quantityProblem(row) != null;
    case 'fuel':
      return fuelOverrideProblem(row) != null;
    case 'description':
      // M7: a lump sum needs a description of what it pays for.
      return row.lumpSum && !(row.lumpSumDescription ?? '').trim();
    case 'rate':
      if (row.rate == null || Number.isNaN(row.rate)) return true;
      // On a lump sum this cell is the amount. $0 would be dropped silently at
      // approve (extractHiredEquipmentData returns null), so require a real figure.
      return row.lumpSum && row.rate <= 0;
  }
}

/**
 * V2-018 / FORM-01: the message shown directly under a field that blocks submit, naming the
 * problem. Empty when the field is fine.
 */
export function requiredFieldMessage(row: HiredEquipmentGridRow, field: RequiredGridField): string {
  if (!isRequiredFieldMissing(row, field)) return '';
  switch (field) {
    case 'date':
      return 'Date required';
    case 'task':
      return 'Task required';
    case 'vendor':
      return 'Vendor required';
    case 'resource':
      return 'Resource required';
    case 'description':
      return 'Description required';
    case 'qty': {
      if (row.qty == null || Number.isNaN(row.qty)) return 'Qty required';
      if (isEachUnit(row.unit) && isNonIntegerQty(row.qty)) return 'Whole number required';
      const problem = quantityProblem(row) ?? '';
      if (/greater than 0/.test(problem)) return 'Qty must be more than 0';
      if (/cannot exceed/.test(problem)) return `Qty ${problem}`;
      if (/whole number/.test(problem)) return 'Whole number required';
      return 'Qty required';
    }
    case 'rate':
      return row.lumpSum ? 'Amount over $0 required' : 'Rate required';
    case 'fuel':
      return fuelOverrideProblem(row) ?? '';
  }
}

export function rowHasMissingRequired(row: HiredEquipmentGridRow): boolean {
  return (
    isRequiredFieldMissing(row, 'date') ||
    isRequiredFieldMissing(row, 'task') ||
    isRequiredFieldMissing(row, 'vendor') ||
    isRequiredFieldMissing(row, 'resource') ||
    isRequiredFieldMissing(row, 'qty') ||
    isRequiredFieldMissing(row, 'rate') ||
    isRequiredFieldMissing(row, 'fuel') ||
    isRequiredFieldMissing(row, 'description')
  );
}

export interface GridValidationResult {
  ok: boolean;
  /** 0-based indexes of incomplete rows. Empty grid → []. */
  invalidIndexes: number[];
  /** 1-based line numbers matching the grid # column. */
  invalidLineNumbers: number[];
  invalidCount: number;
  firstInvalidIndex: number;
}

export function validateGridRows(rows: HiredEquipmentGridRow[]): GridValidationResult {
  if (rows.length === 0) {
    return {
      ok: false,
      invalidIndexes: [],
      invalidLineNumbers: [],
      invalidCount: 0,
      firstInvalidIndex: -1,
    };
  }
  const invalidIndexes = rows.reduce<number[]>((acc, row, index) => {
    if (rowHasMissingRequired(row)) acc.push(index);
    return acc;
  }, []);
  const invalidLineNumbers = invalidIndexes.map((index) => index + 1);
  return {
    ok: invalidIndexes.length === 0,
    invalidIndexes,
    invalidLineNumbers,
    invalidCount: invalidIndexes.length,
    firstInvalidIndex: invalidIndexes[0] ?? -1,
  };
}

/** English list: "2", "2 and 4", "1, 3, and 5". */
export function formatLineNumberList(lineNumbers: number[]): string {
  if (lineNumbers.length === 0) return '';
  if (lineNumbers.length === 1) return String(lineNumbers[0]);
  if (lineNumbers.length === 2) return `${lineNumbers[0]} and ${lineNumbers[1]}`;
  return `${lineNumbers.slice(0, -1).join(', ')}, and ${lineNumbers[lineNumbers.length - 1]}`;
}

export function formatIncompleteLinesMessage(lineNumbers: number[]): string {
  const count = lineNumbers.length;
  if (count === 0) return '';
  const list = formatLineNumberList(lineNumbers);
  if (count === 1) {
    return `1 incomplete line – line ${list} is missing required fields or has an invalid value.`;
  }
  return `${count} incomplete lines – lines ${list} are missing required fields or have invalid values.`;
}

export function gridLineElementId(lineNumber: number): string {
  return `he-grid-line-${lineNumber}`;
}

export function scrollToGridLine(lineNumber: number): void {
  document.getElementById(gridLineElementId(lineNumber))?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
}
